import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, AfterViewInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { SessionService } from "src/app/services/session.service";

import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil, tap } from "rxjs/operators";
import { TaskpanelService, TagItem } from "../../service/taskpanel.service";
import { SubtaskModel, TaskMgmtService } from "../../service/task-mgmt.service";
import { DialogService } from "src/app/services/dialog.service";
import moment from "moment";

interface Option {
  value: string;
  viewValue: string;
}

interface TaskFile {
  name: string;
  size: string;
}

interface AdvancedSearchByOption {
  value: "name" | "id";
  viewValue: string;
}

interface AdvancedColumnConfig {
  field: "id" | "name";
  header: string;
  width?: string;
}

interface TaskUpdate {
  text: string;
  author: string;
  date: string;
  files?: TaskFile[];
}

@Component({
  selector: "app-task-detail-content",
  templateUrl: "./task-detail-content.component.html",
  styleUrls: ["./task-detail-content.component.scss"],
})
export class TaskDetailContentComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  /** Validates that an array form control holds at least `min` items.
   *  Validators.required treats `[]` as valid (truthy), so this fills the gap
   *  for the `tags` control which must require at least one selected tag. */
  static minArrayLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const len = Array.isArray(value) ? value.length : 0;
      return len >= min
        ? null
        : { minArrayLength: { requiredLength: min, actualLength: len } };
    };
  }

  /** Validates that an array form control holds at most `max` items. */
  static maxArrayLength(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const len = Array.isArray(value) ? value.length : 0;
      return len <= max
        ? null
        : { maxArrayLength: { requiredLength: max, actualLength: len } };
    };
  }

  private destroy$ = new Subject<void>();
  private isInitialPatch = true;

  @Input() taskData: any;
  @Input() context: 'dialog' | 'page' = 'dialog';

  @ViewChild('taskNameInput') taskNameInput!: ElementRef;
  /** Scroll target for auto-scroll-to-bottom after comments load / post. This
   *  is the `.task-detail-body#task_mgmt_id` div on the Updates tab — the
   *  scrollable body that contains the comment list. */
  @ViewChild('updatesFeedContainer') updatesFeedContainer?: ElementRef;

  /** Tag search input (used for fixed-position dropdown placement). */
  @ViewChild('tagSearchInput') tagSearchInput?: ElementRef;
  /** Fixed-position results dropdown — coordinates computed from the input's
   *  getBoundingClientRect so it can escape the dialog's overflow:hidden. */
  @ViewChild('tagResultsDropdown') tagResultsDropdown?: ElementRef;

  /** Assignee search input (used for fixed-position dropdown placement). */
  @ViewChild('assigneeSearchInput') assigneeSearchInput?: ElementRef;
  /** Fixed-position assignee results dropdown — same escape-overflow pattern
   *  as the tag search dropdown (positionTagDropdown). */
  @ViewChild('assigneeResultsDropdown') assigneeResultsDropdown?: ElementRef;

  parentTaskInfo: any = null;

  @Output() saved = new EventEmitter<any>();
  // `completed` removed — the "Complete Task" button that emitted this has
  // been replaced by the unified "Update Task" button which emits `saved`.
  // Users now change the status to Done via the dropdown and click Update.
  @Output() cancelled = new EventEmitter<void>();

  taskId = "082190";

  // ========== Top Toggle Tabs ==========
  // A third `Attachments` tab (value: 2) was added alongside Details/Updates.
  // The tab bar is rendered as an *ngFor over `items`, so adding a new entry
  // here (plus the matching `<ng-container *ngIf="selectedType === 2">` block
  // in the template) is all that's needed to light up a new tab.
  items = [
    { title: "Case Details", value: 0 },
    { title: "Case Updates", value: 1 },
    { title: "Attachments", value: 2 },
  ];

  selectedType = 0;

  // ========== Task Type Options ==========
  // Populated from `taskData.taskTypeOptions` (passed by the dialog/parent) in
  // ngOnInit. Each entry shape: { value: <number>, label: <string> }.
  taskTypeOptions: any[] = [];

  // ========== Assignee Chip Input ==========
  assigneeInput = '';
  assigneeInputError: string | null = null;

  // ========== Status Options ==========
  // Populated from `taskData.statusOptions` in ngOnInit.
  statusOptions: any[] = [];

  // ========== Priority Options ==========
  priorityLevelOptions: any[] = [];

  // ========== Tag Categories (API-driven chip selector) ==========
  // Populated from taskData.tagCategories (loaded once at startup in
  // TaskManagementWfComponent). Each entry: { tag_category_id, category_name, ... }
  tagCategories: any[] = [];
  categoryIdCache: Record<string, number> = {};

  /** Chip palette — cycled via getCategoryColor(index). */
  private readonly CATEGORY_COLORS: string[] = ['#1363df', '#185FA5', '#1363df', '#7C3AED', '#1363df', '#0369A1'];

  /** Currently active chip state — mirrors the `tagType` form control. */
  activeTagCategoryName: string = '';
  activeTagCategoryId: number | null = null;

  /** True when the dialog was opened from an entity sidebar (patient/HCP) with
   *  a preset tag. The tag-type chip is then pre-selected AND read-only so the
   *  case stays tagged to that entity type. Set from `taskData.lockTagType`. */
  isTagTypeLocked = false;

  /** True until tagCategories has been populated (either by the parent
   *  passing them through MAT_DIALOG_DATA, or because the parent finished
   *  the startup forkJoin before the dialog opened). Drives the skeleton
   *  chips in the Tag Type field. */
  isLoadingTagCategories = true;

  /** Rich tag selection driving the save payload and the chip strip.
   *  Shape: { type: UPPERCASE category name, id: tag_id, name: tag_name,
   *           category_id: tag_category_id, category: category_name }. */
  selectedEntityLinks: { type: string; id: string; name: string; category_id: number | null; category: string }[] = [];

  /** Current search results (flat list from /search-entities). */
  tagSearchResults: any[] = [];
  isSearchingTags = false;
  showTagResults = false;
  /** Manual debounce handle for the 300ms keystroke delay. */
  private tagSearchTimer: any = null;

  // ========== Lazy-loaded Tags ==========
  tagItems: TagItem[] = [];
  tagPage = 1;
  tagPageSize = 50;
  tagTotal = 0;
  tagLoading = false;
  tagHasMore = true;
  tagSearch$ = new Subject<string>();
  currentTagSearch = "";

  // ========== Forms ==========
  taskForm!: FormGroup;
  updateForm!: FormGroup;

  // ========== Attachments ==========
  attachmentsDisplay = "";
  attachments: File[] = [];

  // ========== Task Updates / Comments (API-backed) ==========
  comments: any[] = [];
  isLoadingComments = false;
  isPostingComment = false;
  editingCommentId: number | null = null;
  editingCommentText = '';
  isSavingEdit = false;
  isDeletingCommentId: number | null = null;
  /** Lazy-load cache key — tracks which task's comments are currently in
   *  `comments[]` so we don't re-fetch when the user toggles tabs back and forth. */
  private commentsLoadedForTaskId: number | null = null;

  // ========== Task Attachments (API-backed) ==========
  //
  // Named `taskAttachments` (not `attachments`) because the legacy
  // `attachments: File[]` field at line 113 is still used by
  // onAttachmentsSelected/removeAttachment for a different form-field upload
  // flow. The two cannot be merged in this scope — renaming the new field
  // keeps the feature isolated without touching unrelated code.
  taskAttachments: any[] = [];
  isLoadingAttachments = false;
  isUploadingFile = false;
  /** 0-100, updated as each file in a multi-file upload completes. */
  uploadProgress = 0;
  isDeletingAttachmentId: number | null = null;
  isDragOver = false;
  /** Same lazy-load cache pattern as commentsLoadedForTaskId — prevents a
   *  re-fetch on every tab toggle back to Attachments. */
  private attachmentsLoadedForTaskId: number | null = null;
  /** 50 MB client-side cap — files larger than this are rejected before
   *  hitting the upload endpoint. */
  private readonly maxFileSize = 50 * 1024 * 1024;

  showStatusDropdown = false;
  currentUserName: string = "";

  // ========== Remind Time (mat-select dropdown) ==========
  readonly remindTimeOptions: readonly { value: string; label: string }[] = this.buildRemindTimeOptions();
  disabledRemindTimes: Set<string> = new Set<string>();
  trackRemindOpt = (_: number, opt: { value: string }): string => opt.value;

  // ========== Assignee Search (user dropdown) ==========
  assigneeSearchResults: any[] = [];
  isSearchingUsers = false;
  showAssigneeResults = false;
  /** Maps usr_email_id → usr_display_nm for chip rendering. */
  assigneeDisplayMap: Record<string, string> = {};
  private assigneeSearchTimer: any = null;
  private assigneeUsersCache: any[] | null = null;

  taskStatus: boolean = false;
  isCancelled: boolean = false;
  dragTargetStatus: string | null = null;
  originalStatus: string | null = null;

  // ========== View / Edit / Create Mode ==========
  isViewMode = false;
  isEditMode = false;
  isCreateMode = false;
  isSubtask = false;
  currentTaskId = '';
  subtasks: SubtaskModel[] = [];

  // ========== Advanced Search ==========
  advancedSearchOpen = false;
  advancedSearchResults: TagItem[] = [];
  advancedSearchLoading = false;
  advancedSearchForm!: FormGroup;
  advancedSearchPage = 1;
  advancedSearchPageSize = 100;
  advancedSearchHasMore = true;
  advancedSearchPerformed = false;
  advancedResultsVisible = true;
  selectedTagMap = new Map<number, TagItem>();

  advancedSearchBy: "name" | "id" = "name";

  advancedSearchByConfigs: { [tagType: string]: AdvancedSearchByOption[] } = {
    "patient-id": [
      { value: "name", viewValue: "Patient Name" },
      { value: "id", viewValue: "Patient ID" },
    ],
    "prescriber-id": [
      { value: "name", viewValue: "Prescriber Name" },
      { value: "id", viewValue: "Prescriber ID" },
    ],
    "account-id": [
      { value: "name", viewValue: "Account Name" },
      { value: "id", viewValue: "Account ID" },
    ],
  };

  advancedColumnConfigs: { [tagType: string]: AdvancedColumnConfig[] } = {
    "patient-id": [
      { field: "id", header: "Patient ID", width: "120px" },
      { field: "name", header: "Patient Name" },
    ],
    "prescriber-id": [
      { field: "id", header: "Prescriber ID", width: "120px" },
      { field: "name", header: "Prescriber Name" },
    ],
    "account-id": [
      { field: "id", header: "Account ID", width: "120px" },
      { field: "name", header: "Account Name" },
    ],
  };

  get currentSearchByOptions(): AdvancedSearchByOption[] {
    const tagType = this.taskForm.get("tagType")?.value;
    return this.advancedSearchByConfigs[tagType] || [];
  }

  get currentColumns(): AdvancedColumnConfig[] {
    const tagType = this.taskForm.get("tagType")?.value;
    return this.advancedColumnConfigs[tagType] || [];
  }

  get selectedTagList(): TagItem[] {
    return Array.from(this.selectedTagMap.values());
  }

  constructor(
    private fb: FormBuilder,
    protected sessionService: SessionService,
    private tagService: TaskpanelService,
    private el: ElementRef,
    private taskService: TaskMgmtService,
    private dialogService: DialogService
  ) {
    // ========== MAIN TASK FORM ==========
    this.taskForm = this.fb.group({
      taskName: ["", Validators.required],
      taskType: [null, Validators.required],
      assignees: [[], [TaskDetailContentComponent.minArrayLength(1)]],
      startDate: [null],
      endDate: [null],
      remindMe: [false],
      remindDate: [null],
      remindTime: [null],
      status: [null, Validators.required],
      priorityLevel: [null, Validators.required],
      tagType: [null, Validators.required],
      // tags must contain at least one entity. Validators.required treats an
      // empty array as "valid" (because [] is truthy), so a custom check is
      // needed. Stays in sync with selectedEntityLinks via onTagResultSelect /
      // removeTagEntity / onTagTypeChipSelect (which clears on category change).
      tags: [[], [TaskDetailContentComponent.minArrayLength(1)]],
      description: [""],
      attachment: [null],
    });
  }

  ngOnInit(): void {
    // ---------- LOOKUP OPTIONS (passed via MAT_DIALOG_DATA) ----------
    if (this.taskData) {
      this.statusOptions = this.taskData.statusOptions || [];
      this.priorityLevelOptions = this.taskData.priorityOptions || [];
      this.taskTypeOptions = this.taskData.taskTypeOptions || [];
      this.tagCategories = this.taskData.tagCategories || [];
      this.categoryIdCache = this.taskData.categoryIdCache || {};
      // Skeleton hides as soon as we have at least one category to render.
      // The dialog cannot currently re-fetch so anything else is treated as
      // "loaded but empty" (the template surfaces that with a hint line).
      this.isLoadingTagCategories = this.tagCategories.length === 0
        && !this.taskData?._tagCategoriesLoaded;
      this.isTagTypeLocked = !!this.taskData?.lockTagType;
    }

    // Coerce status / priority / type ID strings into numbers so they match the
    // mat-option values (which are numbers from the API).
    const toNumOrNull = (v: any): any => {
      if (v == null || v === '') return null;
      const n = typeof v === 'number' ? v : Number(v);
      return isNaN(n) ? v : n;
    };

    // ---------- PATCH TASK DATA ----------
    if (this.taskData) {
      const hasExistingRemind = !!(this.taskData?.remindMe || this.taskData?.remind_me);
      const existingRemindDate = this.taskData?.remindDate || this.taskData?.remind_date || null;
      const existingRemindTime = this.taskData?.remindTime || this.taskData?.remind_time || null;

      this.taskForm.patchValue({
        taskName: (this.taskData.name ?? this.taskData.taskName ?? this.taskData.task_name ?? "").toString().trim(),
        taskType: toNumOrNull(this.taskData.taskType ?? this.taskData.task_type_id),

        startDate: this.taskData.startDate ?? this.taskData.start_date ?? null,
        endDate: this.taskData.endDate ?? this.taskData.end_date ?? null,

        remindMe: hasExistingRemind,
        remindDate: existingRemindDate ? new Date(existingRemindDate) : null,
        remindTime: existingRemindTime || null,

        status: toNumOrNull(this.taskData.status ?? this.taskData.task_status_id),
        priorityLevel: toNumOrNull(this.taskData.priorityLevel ?? this.taskData.task_priority_id) ?? null,

        tagType: this.taskData.tagType ?? null,
        tags: this.taskData.tags ?? [],

        description: (this.taskData.description ?? "").toString().trim(),
        attachment: this.taskData.attachment ?? null,
      });

      // Restore assignees from the task's multi-assignee array, falling back
      // to the legacy single assignTo/owner_id field.
      const existingAssignees = this.taskData?.assignees || this.taskData?._raw?.assignees || [];
      const emails: string[] = Array.isArray(existingAssignees)
        ? existingAssignees
            .map((a: any) => typeof a === 'string' ? a : (a?.assignee_email || ''))
            .filter(Boolean)
        : [];
      if (emails.length === 0) {
        const fallback = this.taskData?.assignTo || this.taskData?.owner_id;
        if (fallback) emails.push(fallback);
      }
      this.taskForm.get('assignees')?.setValue(emails);

      // Populate display name map for assignee chips (edit mode).
      this.assigneeDisplayMap = {};
      if (Array.isArray(existingAssignees)) {
        existingAssignees.forEach((a: any) => {
          if (typeof a === 'object' && a?.assignee_email) {
            this.assigneeDisplayMap[a.assignee_email] = a?.usr_display_nm || a?.display_name || a.assignee_email;
          }
        });
      }

      // Compute disabled time slots for the mat-select dropdown.
      this.recomputeDisabledRemindTimes();

      this.isCancelled = this.taskData.isCancelled;
      this.taskStatus = this.taskData.taskStatus;
      this.isSubtask = !!this.taskData.isSubtask;
      this.showStatusDropdown = !!this.taskData.fromTableView;
      this.dragTargetStatus = this.taskData.dragTargetStatus ?? null;
      this.originalStatus = this.taskData.status ?? null;

      // If caller requests a specific tab (e.g. drag-and-drop opens "Task Updates",
      // or the kanban-card comment icon opens this dialog directly on the Updates tab).
      if (this.taskData.initialTab !== undefined) {
        this.selectedType = this.taskData.initialTab;
      }

      // ---------- MODE DETECTION ----------
      // The old three-state flow (create / view / edit) required the user to
      // click an in-dialog Edit pencil to switch from read-only view mode to
      // the editable form. That intermediate state has been removed — the
      // dialog now opens directly in EDIT mode for existing tasks and CREATE
      // mode for new ones. `isViewMode` is always false.
      this.currentTaskId = this.taskData.task_id || '';
      this.subtasks = (this.taskData.subtasks || []).map((st: SubtaskModel) => ({ ...st }));

      if (!this.taskData.taskStatus && !this.currentTaskId) {
        this.isCreateMode = true;
        this.isViewMode = false;
        this.isEditMode = false;
      } else {
        this.isCreateMode = false;
        this.isViewMode = false;
        this.isEditMode = true;
      }

      if (this.isSingleTagCreateCase) {
        const tagsControl = this.taskForm.get('tags');
        tagsControl?.setValidators([
          TaskDetailContentComponent.minArrayLength(1),
          TaskDetailContentComponent.maxArrayLength(1),
        ]);
        tagsControl?.updateValueAndValidity();
      }

      // ---------- INITIAL STATUS (create mode) ----------
      // Delegates to applyInitialStatus() which:
      //   1. Reads `taskData.presetStatusId` (a numeric API id).
      //   2. Verifies the id exists in the loaded statusOptions.
      //   3. Patches the form.
      //   4. Disables the control if `taskData.lockStatus === true`.
      // ngOnChanges re-runs this whenever `taskData` is swapped, so the status
      // stays correct across taskData changes.
      this.applyInitialStatus();

      // ---------- AUTO-LOAD API CONTENT WHEN DIALOG OPENS ON UPDATES / ATTACHMENTS ----------
      // When the parent passes `initialTab: 1` or `initialTab: 2` (e.g. from
      // the kanban-card comment / attachment icons), that tab is already
      // selected by the time the template renders, so the user never *clicks*
      // the tab and `changeDisplayType(value)` never fires. Trigger the
      // matching lazy-loader directly here. The internal cache keys
      // (`commentsLoadedForTaskId` / `attachmentsLoadedForTaskId`) prevent
      // duplicate fetches when the user clicks the tab again, and the
      // loaders are no-ops in create mode (no task id yet).
      this.loadCommentsIfNeeded();
      this.loadAttachmentsIfNeeded();
    }

    // ---------- USER NAME ----------
    try {
      const raw = sessionStorage.getItem("userInformation");
      if (raw) {
        const parsed = JSON.parse(raw);
        this.currentUserName = parsed?.usr_display_nm || '';
      }
    } catch {
      this.currentUserName = '';
    }

    // ---------- UPDATE FORM ----------
    this.updateForm = this.fb.group({
      message: ["", Validators.required],
    });

    // ---------- ADVANCED SEARCH FORM ----------
    this.advancedSearchForm = this.fb.group({
      searchText: [""],
    });

    // ---------- AUTO-SEARCH (>4 chars, debounced) ----------
    this.advancedSearchForm.get("searchText")?.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((val: string) => {
        if (val && val.length > 4) {
          this.onAdvancedSearch();
        } else if (!val || val.length === 0) {
          this.advancedSearchResults = [];
          this.advancedSearchPerformed = false;
        }
      });

    // ---------- TAG SEARCH (legacy typeahead pipeline) ----------
    // REMOVED: the old in-memory ng-select typeahead + advanced-search panel
    // have been replaced by the chip selector + lazy /search-entities flow.
    // The new pipeline lives in onTagTypeChipSelect / onTagSearchInput /
    // searchTags below. Reset logic that used to live in the old valueChanges
    // subscription is now done inside onTagTypeChipSelect() directly.

    // ---------- EDIT MODE: restore entity tag selection ----------
    // The API preserves the rich tag objects under `_raw.tags`
    // ([{ tag_id, tag_name, category_name, tag_category_id }, ...]). The
    // in-memory TaskModel.tags is coerced to number[] by mapApiResponseToTaskModel,
    // so the helper checks _raw first and falls back to a non-numeric `tags`
    // shape otherwise.
    this.restoreTagsFromTaskData();

    // ---------- REMIND ME TOGGLE BEHAVIOR ----------
    this.taskForm.get("remindMe")?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        this.onRemindMeToggle(!!enabled);
      });

    // Recompute disabled time slots when remind date changes.
    this.taskForm.get('remindDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.recomputeDisabledRemindTimes(); });

    // ---------- FORM IS ALWAYS EDITABLE ----------
    // The old view-mode path disabled the form here. Since the dialog now
    // opens directly in edit mode, the form stays enabled. If a specific
    // control must be locked (e.g. lockStatus), applyInitialStatus() above
    // already handles that independently.

    // Extract parent task info for subtask creation banner
    this.parentTaskInfo = this.taskData?.parentTaskInfo || null;

    // Allow the valueChanges subscription on remindMe to fire the auto-populate
    // logic only AFTER the initial patch is complete. Without this guard, editing
    // a task that already has a reminder would overwrite saved values with defaults.
    setTimeout(() => { this.isInitialPatch = false; }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the parent swaps taskData (e.g. the dialog is reused for a different
    // record), re-apply the initial status AND re-restore tag chips so the
    // mat-select shows the right option and the tag UI reflects the new
    // record's tags. Skipped on the first cycle because ngOnInit handles that path.
    if (changes['taskData'] && !changes['taskData'].firstChange) {
      // Refresh the lookup hand-off — the new taskData may carry an updated
      // tagCategories list (e.g. after the parent finished its forkJoin).
      if (this.taskData) {
        this.tagCategories = this.taskData.tagCategories || this.tagCategories;
        this.categoryIdCache = this.taskData.categoryIdCache || this.categoryIdCache;
        this.isLoadingTagCategories = this.tagCategories.length === 0
          && !this.taskData?._tagCategoriesLoaded;
      }
      this.applyInitialStatus();
      this.restoreTagsFromTaskData();
    }
  }

  /** Apply the initial status value in create mode.
   *
   *  Flow:
   *  1. Only runs in create mode (edit/view modes read status from taskData directly).
   *  2. Reads `taskData.presetStatusId` — a numeric API id resolved by the
   *     parent via TaskMgmtService.getDefaultOpenStatusId / getStatusIdByColumnKey.
   *  3. Verifies the id exists in the currently-loaded statusOptions (defensive —
   *     avoids patching a value that mat-select can't match to any mat-option).
   *  4. Patches the form and optionally disables the control when `lockStatus`.
   *
   *  Callable from ngOnInit (initial) and ngOnChanges (taskData swap).
   */
  private applyInitialStatus(): void {
    if (!this.taskData) return;
    if (!this.isCreateMode) return;

    const presetId = this.taskData.presetStatusId;
    if (presetId == null) return;

    // Verify the id exists in the loaded lookup — mat-select only selects an
    // option whose [value] strictly equals the form value. If the parent hasn't
    // loaded options yet, skip (the value will resolve on the next taskData
    // change — or via the [statusOptions] path if it's ever wired that way).
    if (!this.statusOptions || this.statusOptions.length === 0) return;
    const exists = this.statusOptions.some(
      (opt: any) => opt.value === presetId || Number(opt.value) === Number(presetId)
    );
    if (!exists) return;

    const statusCtrl = this.taskForm.get('status');
    if (!statusCtrl) return;

    statusCtrl.setValue(presetId);

    if (this.taskData.lockStatus === true) {
      statusCtrl.disable();
    } else {
      statusCtrl.enable();
    }
  }

  /** Restore the chip + selectedEntityLinks state from `taskData.tags` (or
   *  `taskData._raw.tags` — the original API payload survives there even
   *  though `mapApiResponseToTaskModel` strips `TaskModel.tags` to numbers).
   *
   *  Called from ngOnInit and again from ngOnChanges when `taskData` is
   *  swapped (e.g. parent reuses the dialog for a different record). Idempotent:
   *  on a no-tags payload it leaves `selectedEntityLinks` empty rather than
   *  clobbering an in-progress user selection.
   *
   *  Order-of-operations note: the dialog can open before `tagCategories` is
   *  populated; the resolved `category_id` will then come straight from the
   *  tag's own `tag_category_id` (which the API always supplies). The
   *  `categoryIdCache` lookup is just a defensive fallback for legacy callers
   *  that send `category_name` only. */
  private restoreTagsFromTaskData(): void {
    if (!this.taskData) return;

    const raw: any = this.taskData?._raw?.tags;
    const plain: any = this.taskData?.tags;
    const sourceTags: any[] = Array.isArray(raw) && raw.length
      ? raw
      : (Array.isArray(plain) ? plain : []);

    const richTags = sourceTags.filter(
      (t: any) => t && typeof t === 'object' && t.tag_id != null
    );
    if (!richTags.length) return;

    this.selectedEntityLinks = richTags.map((tg: any) => {
      const categoryName = String(tg.category_name || '');
      const upper = categoryName.toUpperCase();
      const cachedCatId = this.categoryIdCache[upper];
      const catId = tg.tag_category_id != null
        ? (typeof tg.tag_category_id === 'number' ? tg.tag_category_id : parseInt(String(tg.tag_category_id), 10))
        : (cachedCatId != null ? Number(cachedCatId) : null);
      return {
        type: upper,
        id: String(tg.tag_id),
        name: String(tg.tag_name || ''),
        category_id: catId,
        category: categoryName,
      };
    });

    // Mirror the first tag's category onto the chip + active-state so the
    // chip group renders the right pill highlighted on open.
    const first = this.selectedEntityLinks[0];
    const firstCatId = first.category_id != null
      ? first.category_id
      : (this.categoryIdCache[first.type] != null ? Number(this.categoryIdCache[first.type]) : null);
    if (firstCatId != null) {
      this.taskForm.get('tagType')?.setValue(firstCatId);
      this.activeTagCategoryName = first.type;
      this.activeTagCategoryId = firstCatId;
    }

    // Sync the numeric `tags` form control. setValue triggers re-validation
    // so the min-length check immediately reports valid for restored tasks.
    this.taskForm.get('tags')?.setValue(
      this.selectedEntityLinks
        .map((e) => parseInt(e.id, 10))
        .filter((n) => !isNaN(n))
    );
  }

  ngAfterViewInit(): void {
    if (this.isSubtask && this.isCreateMode && this.taskNameInput) {
      setTimeout(() => {
        this.taskNameInput.nativeElement.focus();
      });
    }
  }

  // ========== TAG PAGINATION HELPERS ==========
  resetTagsPage(): void {
    this.tagPage = 1;
    this.tagTotal = 0;
    this.tagHasMore = true;
    this.tagItems = [];
  }

  loadTagsPage(page: number, searchTerm: string = this.currentTagSearch): void {
    const tagType = this.taskForm.get("tagType")?.value;
    if (!tagType) {
      this.tagItems = [];
      this.tagTotal = 0;
      this.tagHasMore = false;
      return;
    }

    this.tagLoading = true;
    this.tagService
      .getTags(tagType, page, this.tagPageSize, searchTerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (page === 1) {
            this.tagItems = res.items;
          } else {
            this.tagItems = [...this.tagItems, ...res.items];
          }
          this.tagTotal = res.total;
          this.tagPage = page;
          this.tagHasMore = this.tagItems.length < this.tagTotal;
          this.tagLoading = false;
        },
        error: () => {
          this.tagLoading = false;
        },
      });
  }

  onTagScrollEnd(): void {
    if (this.tagHasMore && !this.tagLoading) {
      const nextPage = this.tagPage + 1;
      this.loadTagsPage(nextPage);
    }
  }

  // ========== TAB SWITCH ==========
  changeDisplayType(value: number): void {
    this.selectedType = value;
    // Lazy-load the API-backed content the first time the user switches to
    // Updates or Attachments. No-op in create mode (no task id yet) and no-op
    // if already loaded for this task (tracked via *LoadedForTaskId keys).
    if (value === 1) {
      this.loadCommentsIfNeeded();
      // If comments were already cached, scroll to latest on tab re-entry.
      if (this.comments.length > 0) {
        this.scrollUpdatesToBottom(false);
      }
    } else if (value === 2) {
      this.loadAttachmentsIfNeeded();
    }
  }

  /** Tolerant accessor for option labels — handles both `{ label }` (FieldOption)
   *  and the legacy `{ viewValue }` shape. Public so the template can call it. */
  optionLabelOf(opt: any): string {
    if (!opt) return '';
    return opt.label ?? opt.viewValue ?? '';
  }
  private optionLabel(opt: any): string { return this.optionLabelOf(opt); }

  /** Map a numeric status id (from the form) to a CSS-friendly key
   *  ('planned' / 'in-progress' / 'done' / 'cancelled') for the colored dot class. */
  statusKeyFor(value: any): string {
    const label = this.getStatusLabel(value).toLowerCase().trim();
    if (label === 'open') return 'planned';
    if (label === 'in progress' || label === 'inprogress') return 'in-progress';
    if (label === 'done' || label === 'closed' || label === 'completed') return 'done';
    if (label === 'cancelled' || label === 'canceled') return 'cancelled';
    return '';
  }

  /** Map a numeric priority id (from the form) to a CSS-friendly key
   *  ('low' / 'medium' / 'high') for the colored dot class. */
  priorityKeyFor(value: any): string {
    const label = this.getPriorityLevelLabel(value).toLowerCase().trim();
    if (label === 'low') return 'low';
    if (label === 'medium') return 'medium';
    if (label === 'high') return 'high';
    return '';
  }

  // ========== Status Label Helper ==========
  getStatusLabel(value: any): string {
    if (value == null || value === '') return '';
    const found = this.statusOptions.find((s: any) => s.value === value || s.value === Number(value));
    return this.optionLabel(found);
  }

  // ========== Priority Label Helper ==========
  getPriorityLevelLabel(value: any): string {
    if (value == null || value === '') return '';
    const found = this.priorityLevelOptions.find((p: any) => p.value === value || p.value === Number(value));
    return this.optionLabel(found);
  }

  // ========== Additional Label Helpers ==========
  getTaskTypeLabel(value: any): string {
    if (value == null || value === '') return '';
    const found = this.taskTypeOptions.find((t: any) => t.value === value || t.value === Number(value));
    return this.optionLabel(found);
  }

  getAssignToLabel(value: any): string {
    if (value == null || value === '') return '';
    return String(value);
  }

  // ========== ASSIGNEE CHIP INPUT ==========

  onAssigneeInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addAssigneeFromInput();
    }
  }

  addAssigneeFromInput(): void {
    const email = (this.assigneeInput || '').trim().toLowerCase();
    this.assigneeInputError = null;
    if (!email) return;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      this.assigneeInputError = 'Enter a valid email address';
      return;
    }
    const current: string[] = this.taskForm.get('assignees')?.value || [];
    if (current.includes(email)) {
      this.assigneeInputError = 'Email already added';
      return;
    }
    this.taskForm.get('assignees')?.setValue([...current, email]);
    this.taskForm.get('assignees')?.markAsTouched();
    this.assigneeInput = '';
  }

  removeAssignee(index: number): void {
    const current: string[] = this.taskForm.get('assignees')?.value || [];
    this.taskForm.get('assignees')?.setValue(current.filter((_: any, i: number) => i !== index));
  }

  getFormAssignees(): string[] {
    return this.taskForm.get('assignees')?.value || [];
  }

  getTagTypeLabel(value: any): string {
    if (value == null || value === '') return '';
    const match = this.tagCategories.find(
      (c: any) => c?.tag_category_id === value || Number(c?.tag_category_id) === Number(value)
    );
    return String(match?.category_name || '');
  }

  // ========== Title ==========
  get contentTitle(): string {
    const taskId = this.getTaskId();
    if (!taskId) {
      return this.isSubtask ? 'Create Child Case' : 'Create Case';
    }
    return `Edit Case #${taskId}`;
  }

  // ========== ATTACHMENTS ==========
  onAttachmentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    this.attachments = Array.from(input.files);
    this.attachmentsDisplay = this.attachments.map((f) => f.name).join(", ");

    this.taskForm.patchValue({ attachment: this.attachments });
  }

  // ========== REMOVE ATTACHMENT ==========
  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
    this.attachmentsDisplay = this.attachments.map((f) => f.name).join(", ");
    this.taskForm.patchValue({
      attachment: this.attachments.length ? this.attachments : null,
    });
  }

  // ========================================================
  //  Task Updates / Comments (API-backed)
  // ========================================================

  /** Extract the numeric task id from taskData — tolerates raw API object,
   *  string id, or nested _raw. Returns null in create mode (before save). */
  getTaskId(): number | null {
    const raw = this.taskData?.task_id ?? this.taskData?._raw?.task_id;
    if (raw == null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw).replace(/^[A-Z]+/i, ''), 10);
    return isNaN(n) ? null : n;
  }

  /** Resolve the logged-in user's EMAIL synchronously. Reads the `authUser`
   *  blob that SessionService stores in sessionStorage after the first
   *  `getUserAttributes()` call (runs during login via AWS Amplify
   *  `fetchUserAttributes()`). Falls back to `userInformation.usr_display_nm`
   *  if the authUser blob is missing so we never send an empty string. */
  private getCurrentUserEmail(): string {
    try {
      const authUserRaw = sessionStorage.getItem('authUser');
      if (authUserRaw) {
        const parsed = JSON.parse(authUserRaw);
        const email = parsed?.attributes?.email;
        if (email) return String(email);
      }
    } catch { /* fall through */ }
    try {
      const uiRaw = sessionStorage.getItem('userInformation');
      if (uiRaw) {
        const parsed = JSON.parse(uiRaw);
        return parsed?.usr_display_nm || '';
      }
    } catch { /* fall through */ }
    return '';
  }

  /** Lazy-load comments for the current task. Called from `changeDisplayType(1)`
   *  the first time the user switches to the Task Updates tab. No-op in create
   *  mode (no task_id yet) and no-op if already loaded for this task. */
  private loadCommentsIfNeeded(): void {
    const taskId = this.getTaskId();
    if (!taskId) return;
    if (this.commentsLoadedForTaskId === taskId) return;
    this.isLoadingComments = true;
    this.taskService.getTaskComments(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.comments = Array.isArray(response) ? response
            : (Array.isArray(response?.items) ? response.items
              : (Array.isArray(response?.data) ? response.data : []));
          this.commentsLoadedForTaskId = taskId;
          this.isLoadingComments = false;
          // Jump to the latest comment after the list renders.
          this.scrollUpdatesToBottom(false);
        },
        error: () => {
          this.comments = [];
          this.isLoadingComments = false;
        },
      });
  }

  getCommentInitials(email: string | null | undefined): string {
    if (!email) return '?';
    const prefix = (email.split('@')[0]) || email;
    const parts = prefix.split(/[._\-+]/).filter((p: string) => p.length > 0);
    const first = (parts[0]?.[0] || '').toUpperCase();
    const second = (parts[1]?.[0] || parts[0]?.[1] || '').toUpperCase();
    return (first + second) || '?';
  }

  getRelativeTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return Math.floor(diffSec / 60) + 'm ago';
    if (diffSec < 86400) return Math.floor(diffSec / 3600) + 'h ago';
    if (diffSec < 604800) return Math.floor(diffSec / 86400) + 'd ago';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getFullTimestamp(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB');
  }

  startEditComment(comment: any): void {
    this.editingCommentId = comment?.task_update_id ?? null;
    this.editingCommentText = (comment?.comment_text || '').toString();
  }

  cancelEditComment(): void {
    this.editingCommentId = null;
    this.editingCommentText = '';
  }

  saveEditComment(comment: any): void {
    const text = (this.editingCommentText || '').trim();
    if (!text || this.isSavingEdit) return;
    const taskId = this.getTaskId();
    if (!taskId || comment?.task_update_id == null) return;

    this.isSavingEdit = true;
    this.taskService.editTaskComment(taskId, comment.task_update_id, text)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const idx = this.comments.findIndex((c) => c.task_update_id === comment.task_update_id);
          if (idx > -1) {
            this.comments[idx] = { ...this.comments[idx], comment_text: text };
            this.comments = [...this.comments];
          }
          this.cancelEditComment();
          this.isSavingEdit = false;
          this.dialogService.openSnackBar('Comment updated', 'OK');
        },
        error: () => {
          this.isSavingEdit = false;
        },
      });
  }

  deleteComment(comment: any): void {
    if (this.isDeletingCommentId != null) return;
    const taskId = this.getTaskId();
    if (!taskId || comment?.task_update_id == null) return;

    this.isDeletingCommentId = comment.task_update_id;
    this.taskService.deleteTaskComment(taskId, comment.task_update_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.comments = this.comments.filter((c) => c.task_update_id !== comment.task_update_id);
          this.isDeletingCommentId = null;
          const _taskId = this.getTaskId();
          if (_taskId) this.taskService.emitAndApplyCountChange(_taskId, this.comments.length, undefined);
          this.dialogService.openSnackBar('Comment deleted', 'OK');
        },
        error: () => {
          this.isDeletingCommentId = null;
        },
      });
  }

  // ========== Comment composer helpers ==========

  /** Scroll the Updates tab comment feed to the bottom. `smooth` is true
   *  when the user just posted a comment (visual feedback), false on initial
   *  load / tab switch (instant jump, no animation). Wrapped in setTimeout
   *  to run after Angular has finished rendering the newly-added DOM. */
  private scrollUpdatesToBottom(smooth: boolean = true): void {
    setTimeout(() => {
      const el = this.updatesFeedContainer?.nativeElement;
      if (!el) return;
      if (smooth) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  /** Auto-resize the composer textarea up to 100px max-height. */
  onComposerInput(event: Event): void {
    const ta = event.target as HTMLTextAreaElement;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 100) + 'px';
  }

  /** Enter posts the comment, Shift+Enter inserts a newline. */
  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.updateForm.get('message')?.value?.trim() && !this.isPostingComment && this.getTaskId()) {
        this.onAddUpdate();
      }
    }
  }

  // ========== ADD TASK UPDATE (POST comment) ==========
  //
  // Flow: validate → resolve task_id (must be saved) → resolve current user
  // email → POST to api/tasks/comments/add → on success, append the returned
  // comment to the local array and clear the composer. Button size preserved
  // via the visibility pattern (see template).
  //
  // TODO: Wire attachment upload to API when endpoint is ready. The local
  // pendingUpdateFiles state still exists for the composer's paperclip UI,
  // but nothing is sent to the API yet.
  onAddUpdate(): void {
    if (this.updateForm.invalid || this.isPostingComment) return;
    const message = (this.updateForm.get('message')?.value || '').toString().trim();
    if (!message) return;

    const taskId = this.getTaskId();
    if (!taskId) {
      // Create mode — the task hasn't been saved yet. Template hides the
      // composer via [disabled], but defensively gate the API call too.
      return;
    }

    this.isPostingComment = true;
    const userEmail = this.getCurrentUserEmail();

    this.taskService.addTaskComment(taskId, message, userEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created: any) => {
          if (created && typeof created === 'object' && created.task_update_id != null) {
            this.comments = [...this.comments, created];
          } else {
            // API returned a bare success — synthesise a minimal local entry
            // so the UI updates immediately. Next tab-switch reload hydrates
            // it with the real task_update_id.
            this.comments = [...this.comments, {
              task_update_id: null,
              comment_text: message,
              created_by: userEmail,
              created_time: new Date().toISOString(),
              attachments: [],
            }];
          }
          this.updateForm.reset({ message: '' });
          this.isPostingComment = false;
          // Smooth-scroll to the newly posted comment.
          this.scrollUpdatesToBottom(true);
          const _taskId = this.getTaskId();
          if (_taskId) this.taskService.emitAndApplyCountChange(_taskId, this.comments.length, undefined);
          this.dialogService.openSnackBar('Comment added', 'OK');
        },
        error: () => {
          this.isPostingComment = false;
        },
      });
  }

  // ============================================================
  //  Task Attachments (API-backed)
  //
  //  Lazy-loaded on the first switch to the Attachments tab via
  //  `loadAttachmentsIfNeeded` (triggered from changeDisplayType
  //  AND from the initial-tab auto-load in ngOnInit). Upload uses
  //  multipart/form-data via TaskMgmtService.uploadTaskAttachment.
  //  Download resolves a short-lived presigned S3 URL and opens
  //  it in a new tab so the binary never routes through Angular.
  // ============================================================

  /** Lazy-load attachments for the current task. No-op in create mode
   *  (no task id yet) and no-op if already loaded for this task. */
  private loadAttachmentsIfNeeded(emitCountAfterLoad = false): void {
    const taskId = this.getTaskId();
    if (!taskId) return;
    if (this.attachmentsLoadedForTaskId === taskId) return;
    this.isLoadingAttachments = true;
    this.taskService.getTaskAttachments(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.taskAttachments = Array.isArray(response) ? response
            : (Array.isArray(response?.items) ? response.items
              : (Array.isArray(response?.data) ? response.data : []));
          this.attachmentsLoadedForTaskId = taskId;
          this.isLoadingAttachments = false;
          if (emitCountAfterLoad && taskId) {
            this.taskService.emitAndApplyCountChange(taskId, undefined, this.taskAttachments.length);
          }
        },
        error: () => {
          this.taskAttachments = [];
          this.isLoadingAttachments = false;
        },
      });
  }

  /** Map a filename to a Material icon so the list renders a glyph per file type. */
  getFileIcon(fileName: string): string {
    const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';
    const iconMap: Record<string, string> = {
      pdf: 'picture_as_pdf',
      doc: 'description', docx: 'description',
      xls: 'table_chart', xlsx: 'table_chart', csv: 'table_chart',
      ppt: 'slideshow', pptx: 'slideshow',
      txt: 'article',
      jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', svg: 'image',
      zip: 'folder_zip', rar: 'folder_zip', '7z': 'folder_zip',
      mp4: 'movie', mov: 'movie', avi: 'movie',
      mp3: 'audiotrack', wav: 'audiotrack',
    };
    return iconMap[ext] || 'attach_file';
  }

  /** Human-readable byte size: "23 B" / "1.4 KB" / "2.3 MB". */
  formatFileSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // --- Drag & drop handlers ---

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files?.length) this.uploadFiles(Array.from(files));
  }

  onAttachmentInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.uploadFiles(Array.from(input.files));
      // Reset so the user can re-select the same file if they need to retry.
      input.value = '';
    }
  }

  /** Entry point for both drag-drop and <input type="file"> uploads. Runs
   *  client-side size validation, then kicks off a sequential upload loop so
   *  one failure doesn't block the rest of the batch and progress reflects
   *  real completion, not just queued-up requests. */
  uploadFiles(files: File[]): void {
    const taskId = this.getTaskId();
    if (!taskId) return;
    const userEmail = this.getCurrentUserEmail();

    const validFiles: File[] = [];
    let anyRejected = false;
    for (const f of files) {
      if (f.size > this.maxFileSize) {
        anyRejected = true;
      } else {
        validFiles.push(f);
      }
    }
    if (anyRejected) {
      // Best-effort user feedback without depending on a service not in scope:
      // log to the console; the snackbar would need DialogService which this
      // component doesn't inject.
      console.warn('One or more files exceeded the 50 MB limit and were skipped.');
    }
    if (validFiles.length === 0) return;

    this.uploadNextFile(validFiles, 0, taskId, userEmail);
  }

  /** Recursive sequential-upload helper. Advances to the next file on
   *  success or failure so a single broken file doesn't halt the batch.
   *  Progress reflects completed files (not bytes) — good enough for short
   *  uploads; a per-file bytes-transferred version would need HttpEventType
   *  reporting which the current interceptor doesn't forward. */
  private uploadNextFile(files: File[], index: number, taskId: number, userEmail: string): void {
    if (index >= files.length) {
      this.isUploadingFile = false;
      this.uploadProgress = 0;
      const uploadedCount = files.length;
      this.dialogService.openSnackBar(
        uploadedCount === 1 ? 'Attachment uploaded' : uploadedCount + ' attachments uploaded', 'OK'
      );
      // Force a refresh so newly-uploaded files pick up their server-side
      // `attachment_id` / `uploaded_time` etc. from the backend.
      this.attachmentsLoadedForTaskId = null;
      this.loadAttachmentsIfNeeded(true);
      return;
    }

    this.isUploadingFile = true;
    this.uploadProgress = Math.round((index / files.length) * 100);

    this.taskService.uploadTaskAttachment(taskId, files[index], userEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.uploadProgress = Math.round(((index + 1) / files.length) * 100);
          this.uploadNextFile(files, index + 1, taskId, userEmail);
        },
        error: () => {
          // Continue with the next file on error so the batch can still finish.
          this.uploadNextFile(files, index + 1, taskId, userEmail);
        },
      });
  }

  /** Resolve a presigned URL and open it in a new tab. The API returns
   *  `{ url: '...' }` per the usual pattern, but we also tolerate a raw
   *  string response. noopener prevents the new tab from accessing
   *  `window.opener`. */
  downloadAttachment(att: any): void {
    const taskId = this.getTaskId();
    if (!taskId || att?.attachment_id == null) return;

    this.taskService.getAttachmentPresignedUrl(taskId, att.attachment_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const url = response?.url ?? response?.presigned_url ?? response;
          if (typeof url === 'string' && url) {
            window.open(url, '_blank', 'noopener');
          }
        },
        error: () => { /* swallowed — user can retry */ },
      });
  }

  /** Delete a single attachment. Optimistic: keep the API as source of
   *  truth, wait for the success response before pruning the local array. */
  deleteAttachment(att: any): void {
    if (this.isDeletingAttachmentId != null) return;
    const taskId = this.getTaskId();
    if (!taskId || att?.attachment_id == null) return;

    this.isDeletingAttachmentId = att.attachment_id;
    this.taskService.deleteTaskAttachment(taskId, att.attachment_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.taskAttachments = this.taskAttachments.filter((a) => a.attachment_id !== att.attachment_id);
          this.isDeletingAttachmentId = null;
          const _taskId = this.getTaskId();
          if (_taskId) this.taskService.emitAndApplyCountChange(_taskId, undefined, this.taskAttachments.length);
          this.dialogService.openSnackBar('Attachment deleted', 'OK');
        },
        error: () => {
          this.isDeletingAttachmentId = null;
        },
      });
  }

  // ========== COMPLETE TASK ==========
  // REMOVED: onCompleteTask() — the "Complete Task" button has been replaced
  // by the unified "Update Task" button. Users change the status to Done via
  // the status dropdown and click Update, which flows through the standard
  // onSave() → saved.emit() path. The parent TaskManagementWfComponent's
  // afterClosed handler already runs the authoritative
  // `taskService.canCompleteTask(taskId)` check for completion guards.

  // ========== ADVANCED SEARCH ==========
  toggleAdvancedSearch(): void {
    const tagType = this.taskForm.get("tagType")?.value;
    if (!tagType) return;

    this.advancedSearchOpen = !this.advancedSearchOpen;
    if (this.advancedSearchOpen) {
      this.advancedSearchResults = [];
      this.advancedSearchForm.reset();
      this.advancedSearchBy = "name";
      this.advancedSearchPerformed = false;
      this.advancedResultsVisible = true;
      this.advancedSearchPage = 1;
      this.advancedSearchHasMore = true;

      // Populate selectedTagMap from current form tags
      const selectedIds: number[] = this.taskForm.get("tags")?.value || [];
      this.selectedTagMap.clear();
      for (const id of selectedIds) {
        const item = this.tagItems.find((t) => t.id === id);
        if (item) this.selectedTagMap.set(id, item);
      }
    }
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.advancedSearchOpen || !this.advancedSearchResults.length) return;

    const target = event.target as HTMLElement;
    const resultsEl = this.el.nativeElement.querySelector(".adv-search-results");
    const searchInputEl = this.el.nativeElement.querySelector(".adv-search-text-field");

    const insideResults = resultsEl?.contains(target);
    const insideSearchInput = searchInputEl?.contains(target);

    if (!insideResults && !insideSearchInput) {
      this.advancedResultsVisible = false;
    }
  }

  onAdvancedSearch(): void {
    this.advancedSearchPage = 1;
    this.advancedSearchHasMore = true;
    this.advancedSearchResults = [];
    this.advancedSearchPerformed = true;
    this.advancedResultsVisible = true;
    this.loadAdvancedSearchPage(1);
  }

  loadAdvancedSearchPage(page: number): void {
    const tagType = this.taskForm.get("tagType")?.value;
    if (!tagType) return;

    this.advancedSearchLoading = true;
    const searchText =
      this.advancedSearchForm.get("searchText")?.value || "";

    if (this.advancedSearchBy === "name") {
      this.tagService
        .getTags(tagType, page, this.advancedSearchPageSize, searchText)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => this.applyAdvancedResults(page, res.items, res.total),
          error: () => (this.advancedSearchLoading = false),
        });
    } else {
      this.tagService
        .getTags(tagType, page, this.advancedSearchPageSize, "")
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const filtered = searchText
              ? res.items.filter((t) => t.id.toString().includes(searchText))
              : res.items;
            this.applyAdvancedResults(page, filtered, res.total);
          },
          error: () => (this.advancedSearchLoading = false),
        });
    }
  }

  private applyAdvancedResults(
    page: number,
    items: TagItem[],
    serverTotal: number
  ): void {
    if (page === 1) {
      this.advancedSearchResults = items;
    } else {
      this.advancedSearchResults = [...this.advancedSearchResults, ...items];
    }
    this.advancedSearchPage = page;
    this.advancedSearchHasMore =
      page * this.advancedSearchPageSize < serverTotal;
    this.advancedSearchLoading = false;
  }

  onAdvancedResultsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
      if (this.advancedSearchHasMore && !this.advancedSearchLoading) {
        this.loadAdvancedSearchPage(this.advancedSearchPage + 1);
      }
    }
  }

  isAdvancedItemSelected(item: TagItem): boolean {
    return this.selectedTagMap.has(item.id);
  }

  toggleAdvancedItem(item: TagItem): void {
    if (this.selectedTagMap.has(item.id)) {
      this.selectedTagMap.delete(item.id);
    } else {
      this.selectedTagMap.set(item.id, item);
    }
    this.syncSelectedTagsToForm();
  }

  removeSelectedTag(id: number): void {
    this.selectedTagMap.delete(id);
    this.syncSelectedTagsToForm();
  }

  private syncSelectedTagsToForm(): void {
    const ids = Array.from(this.selectedTagMap.keys());
    this.taskForm.patchValue({ tags: ids });
  }

  // ========== TAG TYPE CHIPS ==========

  /** Chip palette cycled by index. */
  getCategoryColor(index: number): string {
    return this.CATEGORY_COLORS[index % this.CATEGORY_COLORS.length];
  }

  /** Chip click handler — single-select. Switching categories clears the
   *  previous entity selection (a selected tag belongs to exactly one
   *  category; showing cross-category chips would be confusing). */
  onTagTypeChipSelect(category: any): void {
    // Tag type is locked to the entity the dialog was opened for — ignore clicks.
    if (this.isTagTypeLocked) return;

    const currentValue = this.taskForm.get('tagType')?.value;
    const newValue = category?.tag_category_id;

    this.taskForm.get('tagType')?.setValue(newValue);
    this.taskForm.get('tagType')?.markAsTouched();

    if (currentValue !== newValue) {
      this.selectedEntityLinks = [];
      this.taskForm.get('tags')?.setValue([]);
      this.tagSearchResults = [];
      this.showTagResults = false;
    }

    this.activeTagCategoryName = String(category?.category_name || '').toUpperCase();
    this.activeTagCategoryId = newValue != null ? Number(newValue) : null;
  }

  // ========== TAG SEARCH (LAZY) ==========

  /** Top-level Create Case only — one Search & Tag entity allowed. */
  get isSingleTagCreateCase(): boolean {
    return this.isCreateMode && !this.isSubtask;
  }

  getTagSearchPlaceholder(): string {
    if (!this.activeTagCategoryName) return 'Select a tag type first…';
    const n = this.activeTagCategoryName;
    return 'Search ' + n.charAt(0) + n.slice(1).toLowerCase() + '…';
  }

  /** Input handler — only fires the API when the user has typed >=2 chars
   *  (after a 300ms debounce) to keep the board responsive. */
  onTagSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value.trim();
    if (this.tagSearchTimer) {
      clearTimeout(this.tagSearchTimer);
      this.tagSearchTimer = null;
    }
    if (value.length < 2) {
      this.tagSearchResults = [];
      this.showTagResults = false;
      return;
    }
    this.tagSearchTimer = setTimeout(() => this.searchTags(value), 300);
  }

  private searchTags(keyword: string): void {
    if (!this.activeTagCategoryName) return;

    this.isSearchingTags = true;
    this.showTagResults = true;
    this.positionTagDropdown();

    // TODO: wire brand_key from the toolbar brand selector once tasks start
    // filtering by brand. Matches the existing hardcode pattern in
    // TaskMgmtService.loadTasksFromApi / loadAllSubtasksFromApi.
    this.tagService
      .searchEntities(this.activeTagCategoryName, keyword, 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.tagSearchResults = Array.isArray(response)
            ? response
            : (Array.isArray(response?.items) ? response.items
              : (Array.isArray(response?.data) ? response.data : []));
          this.isSearchingTags = false;
          this.positionTagDropdown();
        },
        error: () => {
          this.tagSearchResults = [];
          this.isSearchingTags = false;
        },
      });
  }

  /** Position the fixed-position results dropdown — prefers opening ABOVE
   *  the input (the search field sits low in the dialog and there is rarely
   *  enough room below). Falls back to BELOW only when above cannot fit and
   *  below has more space. Border-radius / shadow are set inline so the
   *  rounded corner is on the side that meets the input. */
  positionTagDropdown(): void {
    const inputEl = this.tagSearchInput?.nativeElement as HTMLElement | undefined;
    const dropdownEl = this.tagResultsDropdown?.nativeElement as HTMLElement | undefined;
    if (!inputEl || !dropdownEl) return;

    const rect = inputEl.getBoundingClientRect();
    const dropdownMaxHeight = 220;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    dropdownEl.style.position = 'fixed';
    dropdownEl.style.left = rect.left + 'px';
    dropdownEl.style.width = rect.width + 'px';

    if (spaceAbove > dropdownMaxHeight || spaceAbove > spaceBelow) {
      // Open ABOVE the input.
      dropdownEl.style.top = 'auto';
      dropdownEl.style.bottom = (window.innerHeight - rect.top + 2) + 'px';
      dropdownEl.style.borderRadius = '8px 8px 2px 2px';
      dropdownEl.style.boxShadow = '0 -6px 24px rgba(0, 0, 0, 0.12)';
    } else {
      // Fallback: open BELOW the input.
      dropdownEl.style.bottom = 'auto';
      dropdownEl.style.top = (rect.bottom + 2) + 'px';
      dropdownEl.style.borderRadius = '2px 2px 8px 8px';
      dropdownEl.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.12)';
    }
  }

  /** Position the assignee results dropdown using `position: fixed` so it
   *  escapes the `.task-detail-body` overflow:auto clipping — same technique
   *  as positionTagDropdown(). Always opens ABOVE the input. */
  positionAssigneeDropdown(): void {
    const inputEl = this.assigneeSearchInput?.nativeElement as HTMLElement | undefined;
    const dropdownEl = this.assigneeResultsDropdown?.nativeElement as HTMLElement | undefined;
    if (!inputEl || !dropdownEl) return;

    const rect = inputEl.getBoundingClientRect();
    dropdownEl.style.position = 'fixed';
    dropdownEl.style.left = rect.left + 'px';
    dropdownEl.style.width = rect.width + 'px';
    dropdownEl.style.bottom = (window.innerHeight - rect.top + 2) + 'px';
    dropdownEl.style.top = 'auto';
  }

  /** Delayed hide — the mousedown on results must fire before blur takes the
   *  dropdown away. 200ms is enough to capture the mousedown event. */
  onTagSearchBlur(): void {
    setTimeout(() => {
      this.showTagResults = false;
    }, 200);
  }

  clearTagSearch(): void {
    this.tagSearchResults = [];
    this.showTagResults = false;
    if (this.tagSearchInput?.nativeElement) {
      this.tagSearchInput.nativeElement.value = '';
    }
  }

  isTagAlreadySelected(tag: any): boolean {
    return this.selectedEntityLinks.some(
      (e) => String(e.id) === String(tag?.tag_id) && e.type === this.activeTagCategoryName
    );
  }

  onTagResultSelect(tag: any): void {
    if (this.isTagAlreadySelected(tag)) return;

    const entity = {
      type: this.activeTagCategoryName,
      id: String(tag?.tag_id),
      name: String(tag?.tag_name || ''),
      category_id: tag?.tag_category_id != null ? Number(tag.tag_category_id) : this.activeTagCategoryId,
      category: String(tag?.category_name || this.activeTagCategoryName),
    };

    if (this.isSingleTagCreateCase) {
      this.selectedEntityLinks = [entity];
    } else {
      this.selectedEntityLinks.push(entity);
    }

    this.taskForm.get('tags')?.setValue(
      this.selectedEntityLinks
        .map((e) => parseInt(e.id, 10))
        .filter((n) => !isNaN(n))
    );

    // Re-render so the freshly-added result shows its ✓ indicator.
    this.tagSearchResults = [...this.tagSearchResults];
  }

  removeTagEntity(index: number): void {
    this.selectedEntityLinks.splice(index, 1);
    this.selectedEntityLinks = [...this.selectedEntityLinks];
    this.taskForm.get('tags')?.setValue(
      this.selectedEntityLinks
        .map((e) => parseInt(e.id, 10))
        .filter((n) => !isNaN(n))
    );
  }

  /** Background tint for the entity chip (category color + low alpha). */
  getEntityChipBg(entity: any): string {
    const color = this.colorForEntity(entity);
    return color + '18';
  }

  /** Border color for the entity chip (category color + medium alpha). */
  getEntityChipBorder(entity: any): string {
    const color = this.colorForEntity(entity);
    return color + '55';
  }

  /** Text color for the entity chip (category color + high alpha). */
  getEntityChipText(entity: any): string {
    const color = this.colorForEntity(entity);
    return color + 'CC';
  }

  private colorForEntity(entity: any): string {
    const idx = this.tagCategories.findIndex(
      (c: any) => String(c?.tag_category_id) === String(entity?.category_id)
    );
    return this.CATEGORY_COLORS[idx >= 0 ? idx % this.CATEGORY_COLORS.length : 0];
  }

  // ========== SAVE ==========
  // The save payload no longer carries subtask state — the inline subtask
  // list that populated `this.subtasks` was removed. Consumers that still
  // read `result.subtasks` from the `saved` emission just see an empty
  // array, which their server-side mappers already treat as "no changes".
  //
  // The `tags` field now holds full rich objects
  // ([{ tag_id, tag_name, tag_category_id }, ...]) to match the updated API
  // contract. TaskMgmtService.mapToCreatePayload forwards this shape
  // unchanged into the create/update request.
  onSave(): void {
    // Touch every control so any pending validation messages (tagType chips,
    // tags min-length) render the moment the user clicks Save.
    this.taskForm.markAllAsTouched();
    if (this.taskForm.invalid) return;

    const raw = this.taskForm.getRawValue();

    // Backward compat: mapToCreatePayload reads assignTo for owner_id
    raw.assignTo = raw.assignees?.[0] || null;
    raw.assignee_emails = raw.assignees || [];

    raw.tags = this.selectedEntityLinks.map((e) => {
      // Resolve tag_category_id with a three-step fallback so the API never
      // receives a null when one is recoverable: explicit on the entity ->
      // categoryIdCache lookup by name -> currently active chip's id.
      const fromEntity = e.category_id != null
        ? (typeof e.category_id === 'number' ? e.category_id : parseInt(String(e.category_id), 10))
        : null;
      const fromCache = (fromEntity == null && e.type && this.categoryIdCache[e.type] != null)
        ? Number(this.categoryIdCache[e.type])
        : null;
      const fromActive = (fromEntity == null && fromCache == null && this.activeTagCategoryId != null)
        ? Number(this.activeTagCategoryId)
        : null;
      const resolved = fromEntity ?? fromCache ?? fromActive;
      if (resolved == null) {
        // Last-resort: send null. Backend can decide; warn so the regression
        // is visible in dev tools.
        // eslint-disable-next-line no-console
        console.warn('Tag missing tag_category_id — sending null', e);
      }
      return {
        tag_id: parseInt(e.id, 10),
        tag_name: e.name,
        tag_category_id: resolved,
      };
    });

    this.saved.emit({ ...raw, subtasks: [] });
  }

  // ========== REMIND ME HELPERS ==========

  /** Called when user toggles Remind Me ON/OFF.
   *  Auto-populates date to today and time to now + 30 min when enabled. */
  onRemindMeToggle(enabled: boolean): void {
    if (this.isInitialPatch) return;
    if (!enabled) {
      this.taskForm.get('remindDate')?.setValue(null);
      this.taskForm.get('remindTime')?.setValue(null);
      return;
    }
    // Preserve existing time if one is already set.
    const existingTime: string | null = this.taskForm.get('remindTime')?.value;
    if (existingTime) return;

    // Compute next 30-min slot strictly after now.
    const now = new Date();
    let h = now.getHours();
    const m = now.getMinutes() < 30 ? 30 : 0;
    if (m === 0) h++;
    let rolledToTomorrow = false;
    if (h >= 24) { h -= 24; rolledToTomorrow = true; }
    const timeStr = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');

    // Set date: today by default, tomorrow if slot wrapped past midnight.
    const existingDate: unknown = this.taskForm.get('remindDate')?.value;
    const needsDate = !existingDate || (rolledToTomorrow && this.isDateToday(existingDate));
    if (needsDate) {
      const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (rolledToTomorrow) base.setDate(base.getDate() + 1);
      this.taskForm.get('remindDate')?.setValue(base);
    }
    this.taskForm.get('remindTime')?.setValue(timeStr);
    this.recomputeDisabledRemindTimes();
  }

  /** minDate for remind date picker — today. */
  get remindMinDate(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private buildRemindTimeOptions(): readonly { value: string; label: string }[] {
    const list: { value: string; label: string }[] = [];
    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        const v = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
        list.push({ value: v, label: v });
      }
    }
    return list;
  }

  /** Recompute which 30-min slots are in the past (today only). */
  recomputeDisabledRemindTimes(): void {
    const dateVal: unknown = this.taskForm.get('remindDate')?.value;
    if (!this.isDateToday(dateVal)) {
      if (this.disabledRemindTimes.size > 0) this.disabledRemindTimes = new Set<string>();
      return;
    }
    const now = new Date();
    const curH = now.getHours();
    const curM = now.getMinutes();
    const next = new Set<string>();
    for (const opt of this.remindTimeOptions) {
      const h = Number(opt.value.substring(0, 2));
      const m = Number(opt.value.substring(3, 5));
      if (h < curH || (h === curH && m <= curM)) next.add(opt.value);
    }
    this.disabledRemindTimes = next;
  }

  private isDateToday(value: unknown): boolean {
    if (!value) return false;
    const d = value instanceof Date ? value : new Date(value as string);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
  }

  /** True when the selected remind time is in the disabled set. */
  get isRemindTimeInPast(): boolean {
    const timeStr: string | null = this.taskForm.get('remindTime')?.value;
    if (!timeStr) return false;
    return this.disabledRemindTimes.has(timeStr);
  }

  // ========== ASSIGNEE SEARCH (user dropdown) ==========

  onAssigneeSearchFocus(): void {
    this.showAssigneeResults = true;
    if (!this.assigneeUsersCache) {
      this.loadAllUsers('');
    }
    this.positionAssigneeDropdown();
  }

  onAssigneeSearchInput(event: Event): void {
    const value = ((event.target as HTMLInputElement).value || '').trim();
    this.showAssigneeResults = true;
    this.positionAssigneeDropdown();
    if (this.assigneeSearchTimer) clearTimeout(this.assigneeSearchTimer);
    this.assigneeSearchTimer = setTimeout(() => this.filterUsers(value), 200);
  }

  onAssigneeSearchBlur(): void {
    setTimeout(() => { this.showAssigneeResults = false; }, 200);
  }

  private loadAllUsers(keyword: string): void {
    this.isSearchingUsers = true;
    this.taskService.getAllUserForImpersonate(keyword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.assigneeUsersCache = Array.isArray(response) ? response : (response?.data || []);
          this.filterUsers(keyword);
          this.isSearchingUsers = false;
          this.positionAssigneeDropdown();
        },
        error: () => {
          this.assigneeUsersCache = [];
          this.assigneeSearchResults = [];
          this.isSearchingUsers = false;
        },
      });
  }

  private filterUsers(keyword: string): void {
    if (!this.assigneeUsersCache) return;
    if (!keyword) {
      this.assigneeSearchResults = this.assigneeUsersCache.slice(0, 50);
      return;
    }
    const lower = keyword.toLowerCase();
    this.assigneeSearchResults = this.assigneeUsersCache
      .filter((u: any) =>
        (u.usr_display_nm || '').toLowerCase().includes(lower) ||
        (u.user_id || '').toLowerCase().includes(lower)
      )
      .slice(0, 50);
  }

  isUserAlreadySelected(user: any): boolean {
    const current: string[] = this.taskForm.get('assignees')?.value || [];
    return current.includes(user?.user_id);
  }

  onUserResultSelect(user: any): void {
    const email = user?.user_id;
    if (!email) return;
    const current: string[] = this.taskForm.get('assignees')?.value || [];
    if (current.includes(email)) return;
    this.taskForm.get('assignees')?.setValue([...current, email]);
    this.taskForm.get('assignees')?.markAsTouched();
    this.assigneeDisplayMap[email] = user?.usr_display_nm || email;
    this.assigneeSearchResults = [...this.assigneeSearchResults];
  }

  getAssigneeDisplayName(email: string): string {
    return this.assigneeDisplayMap?.[email] || email;
  }

  ngOnDestroy(): void {
    if (this.tagSearchTimer) {
      clearTimeout(this.tagSearchTimer);
      this.tagSearchTimer = null;
    }
    if (this.assigneeSearchTimer) {
      clearTimeout(this.assigneeSearchTimer);
      this.assigneeSearchTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
